import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Tailwind-class merger — shadcn/ui Standard-Helper.
 * Erlaubt kombinieren + de-duplizieren von Tailwind-Klassen.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Entfernt die vom Backend an body_text angehaengten Anhang-Text-Dumps
 * ([PDF-ANHANG '...': ...] und [BILD-ANHANG: ...]). Diese werden in
 * services/imap/_sync.py ans Ende des Plain-Text-Bodys gehaengt, damit die KI
 * PDF-Inhalte lesen kann — sie gehoeren aber NICHT in den zitierten Verlauf
 * einer Antwort/Weiterleitung (sonst landet die komplette PDF im gesendeten
 * Mail-Verlauf). Da die Marker immer am Body-Ende stehen, schneiden wir ab dem
 * ersten Marker bis zum Ende ab. body_html ist davon nicht betroffen.
 */
export function stripAttachmentDumps(text: string): string {
  if (!text) return text
  const idx = text.search(/\n?\[(?:PDF|BILD)-ANHANG[: ]/)
  return idx >= 0 ? text.slice(0, idx).trimEnd() : text
}

/** Zeitstempel ohne Zeitzonen-Kennung, z.B. "2026-08-05T19:45:41.830439". */
const OHNE_ZEITZONE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/
/** Reines Datum ohne Uhrzeit, z.B. "2026-08-10". */
const NUR_DATUM = /^\d{4}-\d{2}-\d{2}$/

/**
 * Parst einen Zeitstempel aus dem Backend.
 *
 * Mehrere Tabellen speichern `created_at`/`updated_at` als TEXT aus
 * `datetime.utcnow().isoformat()` — also UTC **ohne** Zeitzonen-Kennung
 * (`tasks.created_at` ist TEXT, siehe database/tasks.py). `new Date()`
 * interpretiert so einen String laut ECMAScript als **lokale** Zeit, wodurch
 * die Anzeige im Sommer 2 Stunden zu frueh war (19:45 statt 21:45).
 * Ohne Kennung deshalb explizit als UTC lesen.
 *
 * Reine Datumsangaben (`due_date`) werden als lokale Mitternacht gebaut —
 * `new Date("2026-08-10")` waere UTC-Mitternacht und koennte je nach Offset
 * auf den Vortag rutschen.
 */
export function parseServerDate(iso?: string | null): Date | null {
  if (!iso) return null
  if (NUR_DATUM.test(iso)) {
    const [j, m, t] = iso.split("-").map(Number)
    return new Date(j, m - 1, t)
  }
  const d = new Date(OHNE_ZEITZONE.test(iso) ? iso + "Z" : iso)
  return isNaN(d.getTime()) ? null : d
}

/**
 * Nimmt dem Modell-Text die Markdown-Syntax.
 *
 * Der Befund aus Takt 1 kommt als freier Fliesstext vom LLM und trug bisher
 * seine Auszeichnung mit ins UI — im Posteingang standen `**Kein Kontakt
 * vorhanden**` samt Sternchen auf dem Schirm. Gerendert wird nichts: der Text
 * ist eine Zusammenfassung, kein Dokument. Aufzaehlungen werden zu einem Satz
 * mit Mittelpunkten, damit aus einer Liste ein Absatz wird.
 */
export function markdownStrippen(text?: string | null): string {
  if (!text) return ""
  const zeilen = text.replace(/\r/g, "").split("\n")
  const punkte: string[] = []
  const absatz: string[] = []
  for (const roh of zeilen) {
    const zeile = roh
      .replace(/^#{1,6}\s+/, "")            // Ueberschriften
      .replace(/\*\*(.+?)\*\*/g, "$1")      // fett
      .replace(/(^|\W)_(.+?)_(\W|$)/g, "$1$2$3") // kursiv mit Unterstrich
      .replace(/`([^`]+)`/g, "$1")          // Code-Ticks
      .trim()
    if (!zeile) continue
    const punkt = zeile.match(/^[-*•]\s+(.*)$/) || zeile.match(/^\d+[.)]\s+(.*)$/)
    if (punkt) punkte.push(punkt[1].replace(/[.;]$/, "").trim())
    else absatz.push(zeile)
  }
  const kopf = absatz.join(" ").replace(/\s+/g, " ").trim()
  const liste = punkte.join(" · ")
  return [kopf, liste].filter(Boolean).join(" ").trim()
}

/**
 * Nur der Name, ohne die Mailadresse in spitzen Klammern.
 *
 * In Listen und Ueberschriften steht der Name — die Adresse gehoert ins
 * Detail. Gemessen am 23.08.2026: „Klaus Testmann <klaus.testmann@…>"
 * brauchte 327 px, verfuegbar waren 247; rund 200 px davon waren reine
 * Adresse. Wer die Adresse braucht, findet sie im Tooltip oder darunter.
 *
 * Gibt es keinen Namen, bleibt die Adresse stehen — dann ist sie das
 * Einzige, was jemanden identifiziert.
 */
export function personName(absender?: string | null, mail?: string | null): string {
  const name = (absender || "").replace(/<[^>]*>/g, "").replace(/^["']|["']$/g, "").trim()
  return name || (mail || "").trim() || (absender || "").trim() || "—"
}
